import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
	fetchCourseLorRequirements,
	refreshCourseLorRequirements,
} from 'api/counselorLor'
import s from './CourseLorRequirements.module.scss'

const POLL_INTERVAL_MS = 4000
// The worker's own timeout is 90s and a message can be retried; stop polling
// well after that rather than leaving an interval running for the session.
const POLL_TIMEOUT_MS = 5 * 60 * 1000

const countLabel = ({ requiredCount, optionalCount }) => {
	const parts = []
	if (Number.isFinite(requiredCount)) {
		parts.push(`${requiredCount} required`)
	}
	if (Number.isFinite(optionalCount) && optionalCount > 0) {
		parts.push(`${optionalCount} optional`)
	}
	return parts.join(' · ')
}

/**
 * What the course asks of a recommender, alongside the letters this student has
 * actually requested. Empty until the `lor` section has been refreshed for the
 * course, which is the normal state for a course nobody has looked at yet - so
 * it renders a prompt to fetch rather than an error.
 */
const CourseLorRequirements = ({ studentId, applicationTargetId }) => {
	const [data, setData] = useState(null)
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState('')
	const pollRef = useRef(null)
	const pollStartedAt = useRef(0)

	const stopPolling = useCallback(() => {
		if (pollRef.current) {
			clearInterval(pollRef.current)
			pollRef.current = null
		}
	}, [])

	const load = useCallback(async () => {
		if (!studentId || !applicationTargetId) return null
		try {
			const res = await fetchCourseLorRequirements(studentId, applicationTargetId)
			setData(res)
			setError('')
			return res
		} catch {
			setError('Could not load recommendation requirements.')
			return null
		}
	}, [studentId, applicationTargetId])

	useEffect(() => {
		let cancelled = false
		setIsLoading(true)
		void load().finally(() => {
			if (!cancelled) setIsLoading(false)
		})
		return () => {
			cancelled = true
			stopPolling()
		}
	}, [load, stopPolling])

	const onRefresh = useCallback(async () => {
		if (!studentId || !applicationTargetId) return
		setError('')
		try {
			await refreshCourseLorRequirements(studentId, applicationTargetId)
		} catch {
			setError('Could not start the refresh.')
			return
		}
		setData(prev => ({ ...(prev || {}), status: 'searching' }))

		stopPolling()
		pollStartedAt.current = Date.now()
		pollRef.current = setInterval(async () => {
			// Give up rather than poll forever if the worker never reports back.
			if (Date.now() - pollStartedAt.current > POLL_TIMEOUT_MS) {
				stopPolling()
				return
			}
			const res = await load()
			if (res && res.status !== 'searching') stopPolling()
		}, POLL_INTERVAL_MS)
	}, [studentId, applicationTargetId, load, stopPolling])

	const status = data?.status || 'idle'
	const topics = data?.topics || []
	const counts = countLabel(data || {})
	// A successful check that found no letters is still a check - say so, rather
	// than showing "0 required" beside "Not checked yet".
	const checked =
		status === 'ready' ||
		Boolean(data?.updatedAt) ||
		Number.isFinite(data?.requiredCount) ||
		topics.length > 0
	const noneRequired =
		checked && !topics.length && !data?.requiredCount && !data?.optionalCount

	return (
		<div className={s.wrap}>
			<div className={s.head}>
				<h4 className={s.title}>What this course asks for</h4>
				<button
					type="button"
					className={s.refresh}
					onClick={onRefresh}
					disabled={status === 'searching'}
				>
					{status === 'searching' ? 'Checking…' : 'Refresh'}
				</button>
			</div>

			{isLoading ? <p className={s.muted}>Loading…</p> : null}
			{error ? <p className={s.error}>{error}</p> : null}

			{!isLoading && !error && counts && !noneRequired ? (
				<p className={s.counts}>{counts}</p>
			) : null}

			{!isLoading && !error && topics.length ? (
				<ul className={s.list}>
					{topics.map((topic, index) => (
						<li className={s.item} key={`${topic.title}-${index}`}>
							<span className={s.item__title}>{topic.title}</span>
							{topic.recommender_type ? (
								<span className={s.item__tag}>{topic.recommender_type}</span>
							) : null}
							{topic.text ? <p className={s.item__text}>{topic.text}</p> : null}
						</li>
					))}
				</ul>
			) : null}

			{!isLoading && !error && noneRequired && status !== 'searching' ? (
				<p className={s.muted}>
					No letters of recommendation required for this course.
				</p>
			) : null}

			{!isLoading && !error && !checked && status !== 'searching' ? (
				<p className={s.muted}>
					Not checked yet. Refresh to look up this course&apos;s recommendation
					requirements.
				</p>
			) : null}

			{status === 'rejected' && data?.error ? (
				<p className={s.error}>{data.error}</p>
			) : null}
		</div>
	)
}

export default CourseLorRequirements
